import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Clock, Copy, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { DNSVerificationService, DomainVerification, DNSRecord } from '@/services/dns-verification-service';

interface DomainVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  currentPlan: string;
}

export const DomainVerificationModal: React.FC<DomainVerificationModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  currentPlan
}) => {
  const [step, setStep] = useState<'input' | 'instructions' | 'verification'>('input');
  const [domain, setDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [verification, setVerification] = useState<DomainVerification | null>(null);
  const [dnsRecords, setDnsRecords] = useState<DNSRecord[]>([]);
  const [verificationStatus, setVerificationStatus] = useState<'checking' | 'verified' | 'failed' | null>(null);
  const { toast } = useToast();

  const isPremiumOrEnterprise = ['premium', 'enterprise'].includes(currentPlan);

  useEffect(() => {
    if (isOpen && step === 'verification' && verification) {
      const interval = setInterval(checkVerificationStatus, 10000); // Check every 10 seconds
      return () => clearInterval(interval);
    }
  }, [isOpen, step, verification]);

  const handleDomainSubmit = async () => {
    if (!domain.trim()) {
      toast({
        title: "Error",
        description: "Please enter a domain name",
        variant: "destructive",
      });
      return;
    }

    if (!isPremiumOrEnterprise) {
      toast({
        title: "Upgrade Required",
        description: "Custom domains require Premium or Enterprise plan",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await DNSVerificationService.claimDomain(workspaceId, domain);
      
      if (result) {
        setVerification(result);
        setDnsRecords(DNSVerificationService.getDNSInstructions(domain, result.verification_token));
        setStep('instructions');
        toast({
          title: "Domain Claimed",
          description: "Please configure the DNS records to verify your domain",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to claim domain. It may already be verified by another workspace.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to claim domain",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkVerificationStatus = async () => {
    if (!verification) return;

    setVerificationStatus('checking');
    try {
      const isVerified = await DNSVerificationService.verifyDNSRecord(verification.id);
      
      if (isVerified) {
        setVerificationStatus('verified');
        toast({
          title: "Domain Verified!",
          description: `Your domain ${domain} has been successfully verified`,
        });
        setTimeout(() => {
          onClose();
          // Refresh the page to apply custom domain
          window.location.reload();
        }, 2000);
      } else {
        setVerificationStatus('failed');
      }
    } catch (error) {
      setVerificationStatus('failed');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "DNS record copied to clipboard",
    });
  };

  const resetModal = () => {
    setStep('input');
    setDomain('');
    setVerification(null);
    setDnsRecords([]);
    setVerificationStatus(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending': return <Clock className="h-4 w-4 text-yellow-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Custom Domain Setup</DialogTitle>
          <DialogDescription>
            Set up a custom domain for your workspace using your company's domain
          </DialogDescription>
        </DialogHeader>

        {!isPremiumOrEnterprise && (
          <Alert>
            <AlertDescription>
              Custom domains are available for Premium and Enterprise plans only. 
              <Button variant="link" className="p-0 h-auto font-semibold">
                Upgrade your plan
              </Button> to use this feature.
            </AlertDescription>
          </Alert>
        )}

        {step === 'input' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="domain">Custom Domain</Label>
              <Input
                id="domain"
                placeholder="manufacturing.tesla.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={!isPremiumOrEnterprise}
              />
              <p className="text-sm text-muted-foreground">
                Enter your full custom domain (e.g., manufacturing.tesla.com, quality.boeing.com)
              </p>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold mb-2">What you'll get:</h4>
              <ul className="text-sm space-y-1">
                <li>• Custom branded workspace URL ({domain || 'manufacturing.yourcompany.com'})</li>
                <li>• Professional appearance with your company branding</li>
                <li>• Enhanced security with domain verification</li>
                <li>• Automatic SSL certificate</li>
              </ul>
            </div>
          </div>
        )}

        {step === 'instructions' && (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                Add these DNS records to your domain provider to verify ownership. 
                Verification typically takes 5-10 minutes after DNS propagation.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              {dnsRecords.map((record, index) => (
                <Card key={index}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Badge variant="outline">{record.type}</Badge>
                      {record.type === 'TXT' ? 'Verification Record' : 'Routing Record'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Name/Host</Label>
                      <div className="flex items-center gap-2">
                        <code className="bg-muted px-2 py-1 rounded text-sm flex-1">
                          {record.name}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(record.name)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Value</Label>
                      <div className="flex items-center gap-2">
                        <code className="bg-muted px-2 py-1 rounded text-sm flex-1 break-all">
                          {record.value}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(record.value)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">TTL</Label>
                      <code className="bg-muted px-2 py-1 rounded text-sm">{record.ttl}</code>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Alert>
              <AlertDescription>
                <strong>Need help?</strong> Check your domain provider's documentation for adding DNS records:
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" asChild>
                    <a href="https://support.cloudflare.com/hc/en-us/articles/360019093151" target="_blank" rel="noopener noreferrer">
                      Cloudflare <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a href="https://docs.aws.amazon.com/Route53/latest/DeveloperGuide/rrsets-working-with.html" target="_blank" rel="noopener noreferrer">
                      AWS Route 53 <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 'verification' && (
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="flex justify-center mb-4">
                {verificationStatus === 'checking' && <Loader2 className="h-8 w-8 animate-spin text-blue-500" />}
                {verificationStatus === 'verified' && <CheckCircle className="h-8 w-8 text-green-500" />}
                {verificationStatus === 'failed' && <XCircle className="h-8 w-8 text-red-500" />}
                {!verificationStatus && <Clock className="h-8 w-8 text-yellow-500" />}
              </div>
              
              <h3 className="text-lg font-semibold mb-2">
                {verificationStatus === 'checking' && 'Checking DNS Records...'}
                {verificationStatus === 'verified' && 'Domain Verified!'}
                {verificationStatus === 'failed' && 'Verification Failed'}
                {!verificationStatus && 'Waiting for DNS Propagation'}
              </h3>
              
              <p className="text-muted-foreground">
                {verificationStatus === 'checking' && 'Please wait while we verify your DNS records.'}
                {verificationStatus === 'verified' && `Your domain ${domain} is now verified and ready to use.`}
                {verificationStatus === 'failed' && 'DNS records not found. Please check your configuration and try again.'}
                {!verificationStatus && 'DNS changes can take up to 24 hours to propagate worldwide.'}
              </p>
            </div>

            {verification && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{verification.domain}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        {getStatusIcon(verification.status)}
                        Status: {verification.status}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={checkVerificationStatus}
                      disabled={verificationStatus === 'checking'}
                    >
                      {verificationStatus === 'checking' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Check Status
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'input' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleDomainSubmit} 
                disabled={!isPremiumOrEnterprise || isLoading}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Claim Domain
              </Button>
            </>
          )}
          
          {step === 'instructions' && (
            <>
              <Button variant="outline" onClick={() => setStep('input')}>
                Back
              </Button>
              <Button onClick={() => setStep('verification')}>
                I've Added the DNS Records
              </Button>
            </>
          )}
          
          {step === 'verification' && (
            <>
              <Button variant="outline" onClick={() => setStep('instructions')}>
                Back to Instructions
              </Button>
              {verificationStatus === 'verified' && (
                <Button onClick={handleClose}>
                  Complete Setup
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 